import React, { useCallback, useEffect, useRef, useState } from 'react';
import './tmi.css';
import './Packlists.css';
import './MasterProjects.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import {
    Grid,
    GridColumn,
    GridFilterChangeEvent,
    GridItemChangeEvent,
    GridCellProps,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

type MilestonesProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: MilestonesState;
    onSaveState?: (menuItemId: number, state: MilestonesState) => void;
};

const PRIMARY_COLUMN_FIELDS = [
    'milestoneOrder',
    'milestoneAltDesc',
        'daysInFutureAllowed',
    'milestoneTypeID',
    'backfillFromMilestoneID',

] as const;

const SECONDARY_COLUMN_FIELDS = [
    'milestoneCode',
    'alwaysEditMilestoneLines',
    'isStocktake',
    'allowMultiScan',
    'doNotCascadeScan',
    'fillFromParts',
    'fillItemsFromPackLists',
    'partialChildrenWeight',
    'rollUpItemsToPackLists',
    'rollUpPackLists',
    'includeTime',
    'packListItemScan',
        'weightedPercentage',
    'lastChangedBy',
    'lastChangedOn',
] as const;

const isColumnVisible = (field: string, showSecondaryColumns: boolean): boolean => {
    if (PRIMARY_COLUMN_FIELDS.includes(field as typeof PRIMARY_COLUMN_FIELDS[number])) {
        return !showSecondaryColumns;
    }

    if (SECONDARY_COLUMN_FIELDS.includes(field as typeof SECONDARY_COLUMN_FIELDS[number])) {
        return showSecondaryColumns;
    }

    return true;
};

interface LookupListItem {
    value: string;
    display: string;
}

interface MilestoneRow {
    clientRowKey: string;
    rowGUID: string;
    milestoneID: number;
    milestoneCode: string;
    milestoneDesc: string;
    milestoneOrder: number;
    milestoneAltDesc: string;
    weightedPercentage: number | null;
    daysInFutureAllowed: number;
    milestoneTypeID: string | null;
    backfillFromMilestoneID: number | null;
    alwaysEditMilestoneLines: boolean;
    isStocktake: boolean;
    allowMultiScan: boolean;
    doNotCascadeScan: boolean;
    fillFromParts: boolean;
    fillItemsFromPackLists: boolean;
    partialChildrenWeight: boolean;
    rollUpItemsToPackLists: boolean;
    rollUpPackLists: boolean;
    includeTime: boolean;
    packListItemScan: boolean;
    lastChangedBy: string;
    lastChangedOn: Date | null;
}

interface MilestoneLineRow {
    MilestoneLineID: number;
    MilestoneID: number;
    MilestoneLineDesc: string;
    ResponseRequired: boolean;
    AttributeClassID: number;
    PDAIsVisible: boolean;
    WebIsVisible: boolean;
    PDACanWrite: boolean;
    WebCanWrite: boolean;
    LastChangedBy: string;
    LastChangedOn: Date | null;
    inEdit?: boolean;
}

interface UserRoleMilestoneRow {
    clientRowKey: string;
    UserRoleMilestoneID: string;
    MilestoneID: number;
    UserRoleID: number | null;
    CanScan: boolean;
    LastChangedBy: string;
    LastChangedOn: Date | null;
    display: string;
    inEdit?: boolean;
}

interface MilestonesState {
    data: MilestoneRow[];
    loading: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filter: FilterDescriptor[];
    filterText: string;
    filterTexts: { [key: string]: string };
    showSecondaryColumns: boolean;
    milestoneTypesDropdown: LookupListItem[];
    backfillMilestonesDropdown: LookupListItem[];
    headerFocusField: string | null;
    editRowKey: string | null;
    originalItem: MilestoneRow | null;
    selectedIds: Set<number>;
    deleteConfirmItem: MilestoneRow | null;
    deleteInProgress: boolean;
    deleteErrorMsg: string;
    newDialogOpen: boolean;
    newDialogSaving: boolean;
    newDialogError: string;
    newDialogMilestoneID: string;
    newDialogCode: string;
    newDialogDesc: string;
    newDialogMilestoneOrder: string;
    newDialogAltDesc: string;
    newDialogWeightedPercentage: string;
    newDialogDaysInFutureAllowed: string;
    newDialogMilestoneTypeID: string;
    newDialogBackfillFromMilestoneID: string;
    newDialogAlwaysEditMilestoneLines: boolean;
    newDialogIsStocktake: boolean;
    newDialogAllowMultiScan: boolean;
    newDialogDoNotCascadeScan: boolean;
    newDialogFillFromParts: boolean;
    newDialogFillItemsFromPackLists: boolean;
    newDialogPartialChildrenWeight: boolean;
    newDialogRollUpItemsToPackLists: boolean;
    newDialogRollUpPackLists: boolean;
    newDialogIncludeTime: boolean;
    newDialogPackListItemScan: boolean;
    menuDataItem: MilestoneRow | null;
    menuAnchor: HTMLElement | null;
    menuAnchorRect: { left: number; top: number; right: number; bottom: number; width: number; height: number } | null;
    menuPosition: { left: number; top: number } | null;
    milestoneLinesDlgOpen: boolean;
    milestoneLinesMilestone: MilestoneRow | null;
    milestoneLinesData: MilestoneLineRow[];
    milestoneLinesDlgLoading: boolean;
    milestoneLinesDlgError: string;
    milestoneLinesSelectedId: number | null;
    milestoneLinesEditingId: number | null;
    milestoneLinesOriginalItem: MilestoneLineRow | null;
    milestoneLinesDeleteConfirmId: number | null;
    milestoneLinesDeleteInProgress: boolean;
    milestoneLinesDeleteError: string;
    milestoneLinesAttributeClassesDropdown: LookupListItem[];
    allowedItemTypesDlgOpen: boolean;
    allowedItemTypesMilestone: MilestoneRow | null;
    allowedItemTypesDropdownData: LookupListItem[];
    allowedItemTypesSelected: Set<string>;
    allowedItemTypesDlgLoading: boolean;
    allowedItemTypesDlgError: string;
    allowedItemTypesDlgFilterText: string;
    allowedPacklistTypesDlgOpen: boolean;
    allowedPacklistTypesMilestone: MilestoneRow | null;
    allowedPacklistTypesDropdownData: LookupListItem[];
    allowedPacklistTypesSelected: Set<string>;
    allowedPacklistTypesDlgLoading: boolean;
    allowedPacklistTypesDlgError: string;
    allowedPacklistTypesDlgFilterText: string;
    userRolesDlgOpen: boolean;
    userRolesMilestone: MilestoneRow | null;
    userRolesDropdownData: LookupListItem[];
    userRolesData: UserRoleMilestoneRow[];
    userRolesDlgLoading: boolean;
    userRolesDlgError: string;
    userRolesEditingKey: string | null;
    userRolesOriginalItem: UserRoleMilestoneRow | null;
    userRolesDeleteConfirmKey: string | null;
    userRolesDeleteInProgress: boolean;
    userRolesDeleteError: string;
}

const normalizeMilestone = (item: any, index: number): MilestoneRow => ({
    clientRowKey: String(item.rowGUID ?? item.RowGUID ?? '') || `milestone-${String(item.milestoneID ?? item.MilestoneID ?? 'new')}-${index}`,
    rowGUID: item.rowGUID ?? item.RowGUID ?? '',
    milestoneID: item.milestoneID ?? item.MilestoneID ?? 0,
    milestoneCode: item.milestoneCode ?? item.MilestoneCode ?? '',
    milestoneDesc: item.milestoneDesc ?? item.MilestoneDesc ?? '',
    milestoneOrder: item.milestoneOrder ?? item.MilestoneOrder ?? 1,
    milestoneAltDesc: item.milestoneAltDesc ?? item.MilestoneAltDesc ?? '',
    weightedPercentage: item.weightedPercentage ?? item.WeightedPercentage ?? null,
    daysInFutureAllowed: item.daysInFutureAllowed ?? item.DaysInFutureAllowed ?? 0,
    milestoneTypeID: item.milestoneTypeID ?? item.MilestoneTypeID ?? null,
    backfillFromMilestoneID: item.backfillFromMilestoneID ?? item.BackfillFromMilestoneID ?? null,
    alwaysEditMilestoneLines: item.alwaysEditMilestoneLines ?? item.AlwaysEditMilestoneLines ?? false,
    isStocktake: item.isStocktake ?? item.IsStocktake ?? false,
    allowMultiScan: item.allowMultiScan ?? item.AllowMultiScan ?? false,
    doNotCascadeScan: item.doNotCascadeScan ?? item.DoNotCascadeScan ?? false,
    fillFromParts: item.fillFromParts ?? item.FillFromParts ?? false,
    fillItemsFromPackLists: item.fillItemsFromPackLists ?? item.FillItemsFromPackLists ?? false,
    partialChildrenWeight: item.partialChildrenWeight ?? item.PartialChildrenWeight ?? false,
    rollUpItemsToPackLists: item.rollUpItemsToPackLists ?? item.RollUpItemsToPackLists ?? false,
    rollUpPackLists: item.rollUpPackLists ?? item.RollUpPackLists ?? false,
    includeTime: item.includeTime ?? item.IncludeTime ?? false,
    packListItemScan: item.packListItemScan ?? item.PackListItemScan ?? false,
    lastChangedBy: item.lastChangedBy ?? item.LastChangedBy ?? '',
    lastChangedOn: (item.lastChangedOn ?? item.LastChangedOn) ? new Date(item.lastChangedOn ?? item.LastChangedOn) : null,
});

const getResponseRowGUID = (response: any): string => response?.stringResponse ?? response?.StringResponse ?? '';

const normalizeMilestoneLine = (item: any): MilestoneLineRow => ({
    MilestoneLineID: item.MilestoneLineID ?? item.milestoneLineID ?? 0,
    MilestoneID: item.MilestoneID ?? item.milestoneID ?? 0,
    MilestoneLineDesc: item.MilestoneLineDesc ?? item.milestoneLineDesc ?? '',
    ResponseRequired: item.ResponseRequired ?? item.responseRequired ?? false,
    AttributeClassID: item.AttributeClassID ?? item.attributeClassID ?? 0,
    PDAIsVisible: item.PDAIsVisible ?? item.pdaIsVisible ?? false,
    WebIsVisible: item.WebIsVisible ?? item.webIsVisible ?? false,
    PDACanWrite: item.PDACanWrite ?? item.pdaCanWrite ?? false,
    WebCanWrite: item.WebCanWrite ?? item.webCanWrite ?? false,
    LastChangedBy: item.LastChangedBy ?? item.lastChangedBy ?? '',
    LastChangedOn: (item.LastChangedOn ?? item.lastChangedOn) ? new Date(item.LastChangedOn ?? item.lastChangedOn) : null,
});

const Milestones: React.FC<MilestonesProps> = (props) => {
    const EDIT_FIELD = 'inEdit';

    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters/sort/dialogs) via stateToLoad/onSaveState,
    // so the page doesn't need to refetch/refilter/reselect. On first visit there is no
    // stateToLoad, so fall back to the defaults below.
    const [state, setState] = useState<MilestonesState>(props.stateToLoad ?? {
        data: [],
        loading: false,
        errorMsg: '',
        sort: [{ field: 'milestoneCode', dir: 'asc' }],
        filter: [],
        filterText: '',
        filterTexts: {},
        showSecondaryColumns: false,
        milestoneTypesDropdown: [],
        backfillMilestonesDropdown: [],
        headerFocusField: null,
        editRowKey: null,
        originalItem: null,
        selectedIds: new Set<number>(),
        deleteConfirmItem: null,
        deleteInProgress: false,
        deleteErrorMsg: '',
        newDialogOpen: false,
        newDialogSaving: false,
        newDialogError: '',
        newDialogMilestoneID: '',
        newDialogCode: '',
        newDialogDesc: '',
        newDialogMilestoneOrder: '1',
        newDialogAltDesc: '',
        newDialogWeightedPercentage: '',
        newDialogDaysInFutureAllowed: '0',
        newDialogMilestoneTypeID: '',
        newDialogBackfillFromMilestoneID: '',
        newDialogAlwaysEditMilestoneLines: false,
        newDialogIsStocktake: false,
        newDialogAllowMultiScan: false,
        newDialogDoNotCascadeScan: false,
        newDialogFillFromParts: false,
        newDialogFillItemsFromPackLists: false,
        newDialogPartialChildrenWeight: false,
        newDialogRollUpItemsToPackLists: false,
        newDialogRollUpPackLists: false,
        newDialogIncludeTime: false,
        newDialogPackListItemScan: false,
        menuDataItem: null,
        menuAnchor: null,
        menuAnchorRect: null,
        menuPosition: null,
        milestoneLinesDlgOpen: false,
        milestoneLinesMilestone: null,
        milestoneLinesData: [],
        milestoneLinesDlgLoading: false,
        milestoneLinesDlgError: '',
        milestoneLinesSelectedId: null,
        milestoneLinesEditingId: null,
        milestoneLinesOriginalItem: null,
        milestoneLinesDeleteConfirmId: null,
        milestoneLinesDeleteInProgress: false,
        milestoneLinesDeleteError: '',
        milestoneLinesAttributeClassesDropdown: [],
        allowedItemTypesDlgOpen: false,
        allowedItemTypesMilestone: null,
        allowedItemTypesDropdownData: [],
        allowedItemTypesSelected: new Set<string>(),
        allowedItemTypesDlgLoading: false,
        allowedItemTypesDlgError: '',
        allowedItemTypesDlgFilterText: '',
        allowedPacklistTypesDlgOpen: false,
        allowedPacklistTypesMilestone: null,
        allowedPacklistTypesDropdownData: [],
        allowedPacklistTypesSelected: new Set<string>(),
        allowedPacklistTypesDlgLoading: false,
        allowedPacklistTypesDlgError: '',
        allowedPacklistTypesDlgFilterText: '',
        userRolesDlgOpen: false,
        userRolesMilestone: null,
        userRolesDropdownData: [],
        userRolesData: [],
        userRolesDlgLoading: false,
        userRolesDlgError: '',
        userRolesEditingKey: null,
        userRolesOriginalItem: null,
        userRolesDeleteConfirmKey: null,
        userRolesDeleteInProgress: false,
        userRolesDeleteError: '',
    });

    const stateRef = useRef<MilestonesState>(state);
    stateRef.current = state;
    const headerCellRenderersRef = useRef<{ [key: string]: (props: any) => JSX.Element }>({});
    const menuRef = useRef<HTMLDivElement | null>(null);
    const menuBlurTimeoutRef = useRef<number | null>(null);
    const userInfRef = useRef(props.userInf);
    const updateUserInfoRef = useRef(props.updateUserInfo);

    const keepEditInputFocus = useCallback((rowKey: string, field: string, selectionStart: number | null, selectionEnd: number | null) => {
        requestAnimationFrame(() => {
            const selector = `input[data-edit-cell="${rowKey}::${field}"]`;
            const el = document.querySelector(selector) as HTMLInputElement | null;
            if (!el) return;
            el.focus();
            if (selectionStart !== null && selectionEnd !== null) {
                try {
                    el.setSelectionRange(selectionStart, selectionEnd);
                } catch {
                    // Ignore non-text input selection errors.
                }
            }
        });
    }, []);

    useEffect(() => {
        userInfRef.current = props.userInf;
    }, [props.userInf]);

    useEffect(() => {
        updateUserInfoRef.current = props.updateUserInfo;
    }, [props.updateUserInfo]);

    const fetchData = useCallback(() => {
        setState(prev => ({ ...prev, loading: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMilestones';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK':
                        setState(prev => ({
                            ...prev,
                            data: Array.isArray(res.results) ? res.results.map((item: any, index: number) => normalizeMilestone(item, index)) : [],
                            loading: false,
                        }));
                        break;
                    case 'UNAUTH': {
                        const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                        updateUserInfoRef.current(uInf);
                        setState(prev => ({ ...prev, loading: false }));
                        break;
                    }
                    default:
                        setState(prev => ({ ...prev, errorMsg: 'Error fetching milestones: ' + (res.callStatusMessage || ''), loading: false }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching milestones: ' + err.toString(), loading: false }));
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    const fetchMilestoneTypesDropdown = useCallback(() => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMilestoneTypesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                    updateUserInfoRef.current(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, milestoneTypesDropdown: res.results }));
                }
            })
            .catch(() => {
                // Do not block page usage when dropdown data is unavailable.
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    const fetchBackfillMilestonesDropdown = useCallback(() => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMilestonesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                    updateUserInfoRef.current(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, backfillMilestonesDropdown: res.results }));
                }
            })
            .catch(() => {
                // Do not block page usage when dropdown data is unavailable.
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    const fetchAttributeClassesDropdown = useCallback(() => {
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetAttributeClassesForDropDown';
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...userInfRef.current, isAuthorised: false };
                    updateUserInfoRef.current(uInf);
                } else if (res.callStatus === 'OK' && Array.isArray(res.results)) {
                    setState(prev => ({ ...prev, milestoneLinesAttributeClassesDropdown: res.results }));
                }
            })
            .catch(() => {
                // Do not block milestone line editing if lookup fetch fails.
            });
    }, [props.userInf.currProject.apiUrl, props.userInf.token]);

    useEffect(() => {
        fetchData();
        fetchMilestoneTypesDropdown();
        fetchBackfillMilestonesDropdown();
        return () => {
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
        };
    }, [fetchData, fetchMilestoneTypesDropdown, fetchBackfillMilestonesDropdown]);

    const handleFilterChange = useCallback((e: GridFilterChangeEvent) => {
        setState(prev => ({ ...prev, filter: [] }));
    }, []);

    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, sort: newSort };
        });
    }, []);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const val = e.target.value;
        setState(prev => ({ ...prev, filterTexts: { ...prev.filterTexts, [field]: val } }));
    }, []);

    const handleItemChange = (e: GridItemChangeEvent) => {
        const edited = e.dataItem as MilestoneRow;
        setState(prev => ({
            ...prev,
            data: prev.data.map(item => item.clientRowKey === edited.clientRowKey ? edited : item),
        }));
    };

    const handleEnterEdit = (dataItem: MilestoneRow) => {
        setState(prev => ({
            ...prev,
            editRowKey: dataItem.clientRowKey,
            originalItem: { ...dataItem },
        }));
    };

    const handleCancel = () => {
        if (!state.originalItem) return;
        const origItem = state.originalItem;
        setState(prev => ({
            ...prev,
            data: prev.data.map(item => item.clientRowKey === origItem.clientRowKey ? origItem : item),
            editRowKey: null,
            originalItem: null,
        }));
    };

    const handleSave = (dataItem: MilestoneRow) => {
        const milestoneOrder = Number(dataItem.milestoneOrder);
        if (!Number.isInteger(milestoneOrder)) {
            setState(prev => ({ ...prev, errorMsg: 'Milestone Order must be a whole number.' }));
            return;
        }

        setState(prev => ({ ...prev, loading: true }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMilestone';

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                RowGUID: dataItem.rowGUID,
                MilestoneID: dataItem.milestoneID,
                MilestoneDesc: dataItem.milestoneDesc,
                MilestoneOrder: milestoneOrder,
                WeightedPercentage: dataItem.weightedPercentage,
                AlwaysEditMilestoneLines: dataItem.alwaysEditMilestoneLines,
                IsStocktake: dataItem.isStocktake,
                AllowMultiScan: dataItem.allowMultiScan,
                DoNotCascadeScan: dataItem.doNotCascadeScan,
                MilestoneTypeID: dataItem.milestoneTypeID,
                FillFromParts: dataItem.fillFromParts,
                FillItemsFromPackLists: dataItem.fillItemsFromPackLists,
                PartialChildrenWeight: dataItem.partialChildrenWeight,
                MilestoneCode: dataItem.milestoneCode,
                DaysInFutureAllowed: dataItem.daysInFutureAllowed,
                RollUpItemsToPackLists: dataItem.rollUpItemsToPackLists,
                BackfillFromMilestoneID: dataItem.backfillFromMilestoneID,
                RollUpPackLists: dataItem.rollUpPackLists,
                MilestoneAltDesc: dataItem.milestoneAltDesc,
                IncludeTime: dataItem.includeTime,
                PackListItemScan: dataItem.packListItemScan,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, loading: false }));
                } else if (res.callStatus === 'OK') {
                    const savedRowGUID = getResponseRowGUID(res) || dataItem.rowGUID;
                    setState(prev => ({
                        ...prev,
                        data: prev.data.map(item => item.clientRowKey === dataItem.clientRowKey ? { ...item, rowGUID: savedRowGUID } : item),
                        editRowKey: null,
                        originalItem: null,
                        loading: false,
                    }));
                    fetchData();
                } else {
                    setState(prev => ({ ...prev, loading: false, errorMsg: 'Error saving milestone: ' + (res.callStatusMessage || '') }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, loading: false, errorMsg: 'Error saving milestone: ' + err.toString() }));
            });
    };

    const openDeleteConfirm = (item: MilestoneRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: item, deleteErrorMsg: '' }));
    };

    const closeDeleteConfirm = () => {
        setState(prev => ({ ...prev, deleteConfirmItem: null, deleteInProgress: false, deleteErrorMsg: '' }));
    };

    const confirmDelete = () => {
        if (!state.deleteConfirmItem) {
            return;
        }

        setState(prev => ({ ...prev, deleteInProgress: true, deleteErrorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteMilestone';
        const rowGUID = state.deleteConfirmItem.rowGUID;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(rowGUID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, deleteInProgress: false }));
                } else if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        deleteConfirmItem: null,
                        deleteInProgress: false,
                        deleteErrorMsg: '',
                    }));
                    fetchData();
                } else {
                    setState(prev => ({
                        ...prev,
                        deleteInProgress: false,
                        deleteErrorMsg: res.callStatusMessage || 'Milestone could not be deleted.',
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    deleteInProgress: false,
                    deleteErrorMsg: 'Error deleting milestone: ' + err.toString(),
                }));
            });
    };

    const openNewDialog = () => {
        setState(prev => ({
            ...prev,
            newDialogOpen: true,
            newDialogSaving: false,
            newDialogError: '',
            newDialogMilestoneID: '',
            newDialogCode: '',
            newDialogDesc: '',
            newDialogMilestoneOrder: '1',
            newDialogAltDesc: '',
            newDialogWeightedPercentage: '',
            newDialogDaysInFutureAllowed: '0',
            newDialogMilestoneTypeID: '',
            newDialogBackfillFromMilestoneID: '',
            newDialogAlwaysEditMilestoneLines: false,
            newDialogIsStocktake: false,
            newDialogAllowMultiScan: false,
            newDialogDoNotCascadeScan: false,
            newDialogFillFromParts: false,
            newDialogFillItemsFromPackLists: false,
            newDialogPartialChildrenWeight: false,
            newDialogRollUpItemsToPackLists: false,
            newDialogRollUpPackLists: false,
            newDialogIncludeTime: false,
            newDialogPackListItemScan: false,
        }));
    };

    const closeNewDialog = () => {
        setState(prev => ({
            ...prev,
            newDialogOpen: false,
            newDialogSaving: false,
            newDialogError: '',
        }));
    };

    const toggleColumnVisibility = useCallback(() => {
        setState(prev => ({ ...prev, showSecondaryColumns: !prev.showSecondaryColumns }));
    }, []);

    const submitNew = () => {
        const milestoneIdValue = state.newDialogMilestoneID.trim();
        const milestoneID = Number(milestoneIdValue);
        if (!milestoneIdValue || !Number.isInteger(milestoneID) || milestoneID < 0) {
            setState(prev => ({ ...prev, newDialogError: 'Milestone ID is required and must be a valid whole number.' }));
            return;
        }

        const weightedValue = state.newDialogWeightedPercentage.trim();
        const weightedPercentage = weightedValue === '' ? null : Number(weightedValue);
        if (weightedValue !== '' && Number.isNaN(weightedPercentage)) {
            setState(prev => ({ ...prev, newDialogError: 'Weighted % must be a valid number.' }));
            return;
        }

        const milestoneOrderValue = state.newDialogMilestoneOrder.trim();
        const milestoneOrder = Number(milestoneOrderValue);
        if (!milestoneOrderValue || !Number.isInteger(milestoneOrder)) {
            setState(prev => ({ ...prev, newDialogError: 'Milestone Order is required and must be a whole number.' }));
            return;
        }

        const daysValue = state.newDialogDaysInFutureAllowed.trim();
        const daysInFutureAllowed = daysValue === '' ? 0 : Number(daysValue);
        if (!Number.isInteger(daysInFutureAllowed)) {
            setState(prev => ({ ...prev, newDialogError: 'Days In Future Allowed must be a whole number.' }));
            return;
        }

        const backfillValue = state.newDialogBackfillFromMilestoneID.trim();
        const backfillFromMilestoneID = backfillValue === '' ? null : Number(backfillValue);
        if (
            backfillValue !== ''
            && (
                backfillFromMilestoneID === null
                || !Number.isInteger(backfillFromMilestoneID)
                || backfillFromMilestoneID < 0
            )
        ) {
            setState(prev => ({ ...prev, newDialogError: 'Backfill From Milestone ID must be a valid whole number.' }));
            return;
        }

        const milestoneTypeID = state.newDialogMilestoneTypeID.trim() || null;

        setState(prev => ({ ...prev, newDialogSaving: true, newDialogError: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMilestone';

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                RowGUID: '',
                MilestoneID: milestoneID,
                MilestoneCode: state.newDialogCode.trim(),
                MilestoneDesc: state.newDialogDesc.trim(),
                MilestoneOrder: milestoneOrder,
                MilestoneAltDesc: state.newDialogAltDesc.trim(),
                WeightedPercentage: weightedPercentage,
                DaysInFutureAllowed: daysInFutureAllowed,
                MilestoneTypeID: milestoneTypeID,
                BackfillFromMilestoneID: backfillFromMilestoneID,
                AlwaysEditMilestoneLines: state.newDialogAlwaysEditMilestoneLines,
                IsStocktake: state.newDialogIsStocktake,
                AllowMultiScan: state.newDialogAllowMultiScan,
                DoNotCascadeScan: state.newDialogDoNotCascadeScan,
                FillFromParts: state.newDialogFillFromParts,
                FillItemsFromPackLists: state.newDialogFillItemsFromPackLists,
                PartialChildrenWeight: state.newDialogPartialChildrenWeight,
                RollUpItemsToPackLists: state.newDialogRollUpItemsToPackLists,
                RollUpPackLists: state.newDialogRollUpPackLists,
                IncludeTime: state.newDialogIncludeTime,
                PackListItemScan: state.newDialogPackListItemScan,
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, newDialogSaving: false }));
                } else if (res.callStatus === 'OK') {
                    getResponseRowGUID(res);
                    closeNewDialog();
                    fetchData();
                } else {
                    setState(prev => ({ ...prev, newDialogSaving: false, newDialogError: res.callStatusMessage || 'Error creating milestone.' }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, newDialogSaving: false, newDialogError: 'Error creating milestone: ' + err.toString() }));
            });
    };

    const openRowMenu = (dataItem: MilestoneRow, anchor: HTMLElement | null) => {
        if (anchor) {
            const anchorRect = anchor.getBoundingClientRect();
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: anchorRect, menuPosition: null }));
        } else {
            setState(prev => ({ ...prev, menuDataItem: dataItem, menuAnchor: anchor, menuAnchorRect: null, menuPosition: null }));
        }
    };

    const closeRowMenu = () => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null, menuAnchorRect: null, menuPosition: null }));
    };

    const handleMenuBlur = () => {
        if (menuBlurTimeoutRef.current) {
            window.clearTimeout(menuBlurTimeoutRef.current);
        }
        menuBlurTimeoutRef.current = window.setTimeout(() => {
            closeRowMenu();
        }, 120);
    };

    const openMilestoneLinesDialog = (milestone: MilestoneRow) => {
        fetchAttributeClassesDropdown();
        setState(prev => ({
            ...prev,
            milestoneLinesDlgOpen: true,
            milestoneLinesMilestone: milestone,
            milestoneLinesData: [],
            milestoneLinesDlgLoading: true,
            milestoneLinesDlgError: '',
            milestoneLinesSelectedId: null,
            milestoneLinesEditingId: null,
            milestoneLinesOriginalItem: null,
            milestoneLinesDeleteConfirmId: null,
            milestoneLinesDeleteInProgress: false,
            milestoneLinesDeleteError: '',
        }));

        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMilestoneLines?MilestoneID=' + milestone.milestoneID;
        fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, milestoneLinesDlgLoading: false }));
                } else if (res.callStatus === 'OK') {
                    setState(prev => ({
                        ...prev,
                        milestoneLinesData: Array.isArray(res.results) ? res.results.map((r: any) => normalizeMilestoneLine(r)) : [],
                        milestoneLinesDlgLoading: false,
                    }));
                } else {
                    setState(prev => ({
                        ...prev,
                        milestoneLinesDlgLoading: false,
                        milestoneLinesDlgError: 'Failed to load milestone lines: ' + (res.callStatusMessage || ''),
                    }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, milestoneLinesDlgLoading: false, milestoneLinesDlgError: 'Error loading milestone lines: ' + err.toString() }));
            });
    };

    const closeMilestoneLinesDialog = () => {
        setState(prev => ({
            ...prev,
            milestoneLinesDlgOpen: false,
            milestoneLinesMilestone: null,
            milestoneLinesData: [],
            milestoneLinesDlgLoading: false,
            milestoneLinesDlgError: '',
            milestoneLinesSelectedId: null,
            milestoneLinesEditingId: null,
            milestoneLinesOriginalItem: null,
            milestoneLinesDeleteConfirmId: null,
            milestoneLinesDeleteInProgress: false,
            milestoneLinesDeleteError: '',
        }));
    };

    const openAllowedItemTypesDialog = (milestone: MilestoneRow) => {
        setState(prev => ({
            ...prev,
            allowedItemTypesDlgOpen: true,
            allowedItemTypesMilestone: milestone,
            allowedItemTypesDropdownData: [],
            allowedItemTypesSelected: new Set<string>(),
            allowedItemTypesDlgLoading: true,
            allowedItemTypesDlgError: '',
            allowedItemTypesDlgFilterText: '',
        }));

        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetMilestoneItemTypesForDropDown?MilestoneID=${milestone.milestoneID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetItemTypesForDropDown`;

        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                if (dataRes.callStatus === 'UNAUTH' || dropdownRes.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, allowedItemTypesDlgLoading: false }));
                    return;
                }

                let dropdownData: LookupListItem[] = [];
                let selectedIds = new Set<string>();
                let hasError = false;

                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                } else {
                    hasError = true;
                }

                if (dataRes.callStatus === 'OK' && Array.isArray(dataRes.results)) {
                    selectedIds = new Set(
                        (dataRes.results as any[])
                            .map((item: any) => {
                                if (typeof item === 'number' || typeof item === 'string') return String(item);
                                if (!item || typeof item !== 'object') return '';
                                return String(
                                    item.itemTypeID
                                    ?? item.ItemTypeID
                                    ?? item.allowedItemTypeID
                                    ?? item.AllowedItemTypeID
                                    ?? item.id
                                    ?? item.ID
                                    ?? ''
                                );
                            })
                            .filter((id: string) => id !== '')
                    );
                } else {
                    hasError = true;
                }

                setState(prev => ({
                    ...prev,
                    allowedItemTypesDropdownData: dropdownData,
                    allowedItemTypesSelected: selectedIds,
                    allowedItemTypesDlgLoading: false,
                    allowedItemTypesDlgError: hasError ? ((dataRes.callStatusMessage || dropdownRes.callStatusMessage || 'Error loading allowed item types')) : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    allowedItemTypesDlgLoading: false,
                    allowedItemTypesDlgError: 'Error loading allowed item types: ' + err.toString(),
                }));
            });
    };

    const closeAllowedItemTypesDialog = () => {
        setState(prev => ({
            ...prev,
            allowedItemTypesDlgOpen: false,
            allowedItemTypesMilestone: null,
            allowedItemTypesDropdownData: [],
            allowedItemTypesSelected: new Set<string>(),
            allowedItemTypesDlgLoading: false,
            allowedItemTypesDlgError: '',
            allowedItemTypesDlgFilterText: '',
        }));
    };

    const handleAllowedItemTypesFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, allowedItemTypesDlgFilterText: e.target.value }));
    };

    const toggleAllowedItemTypeSelection = (itemTypeId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.allowedItemTypesSelected);
            if (newSelected.has(itemTypeId)) {
                newSelected.delete(itemTypeId);
            } else {
                newSelected.add(itemTypeId);
            }

            if (prev.allowedItemTypesMilestone) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMilestoneItemType';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.allowedItemTypesMilestone.milestoneID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)).filter(id => Number.isFinite(id)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({ ...s, allowedItemTypesDlgError: 'Error saving: ' + (res.callStatusMessage || '') }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({ ...s, allowedItemTypesDlgError: 'Error saving: ' + err.toString() }));
                    });
            }

            return { ...prev, allowedItemTypesSelected: newSelected };
        });
    };

    const openAllowedPacklistTypesDialog = (milestone: MilestoneRow) => {
        setState(prev => ({
            ...prev,
            allowedPacklistTypesDlgOpen: true,
            allowedPacklistTypesMilestone: milestone,
            allowedPacklistTypesDropdownData: [],
            allowedPacklistTypesSelected: new Set<string>(),
            allowedPacklistTypesDlgLoading: true,
            allowedPacklistTypesDlgError: '',
            allowedPacklistTypesDlgFilterText: '',
        }));

        const selectedUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetMilestonePackListTypesForDropDown?MilestoneID=${milestone.milestoneID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypesForDropDown`;

        Promise.all([
            fetch(selectedUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                if (dataRes.callStatus === 'UNAUTH' || dropdownRes.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, allowedPacklistTypesDlgLoading: false }));
                    return;
                }

                let dropdownData: LookupListItem[] = [];
                let selectedIds = new Set<string>();
                let hasError = false;

                if (dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)) {
                    dropdownData = dropdownRes.results.map((item: any) => ({
                        value: item.value || item.id?.toString(),
                        display: item.display || item.name,
                    }));
                } else {
                    hasError = true;
                }

                if (dataRes.callStatus === 'OK' && Array.isArray(dataRes.results)) {
                    selectedIds = new Set(
                        (dataRes.results as any[])
                            .map((item: any) => {
                                if (typeof item === 'number' || typeof item === 'string') return String(item);
                                if (!item || typeof item !== 'object') return '';
                                return String(
                                    item.packListTypeID
                                    ?? item.PackListTypeID
                                    ?? item.allowedPackListTypeID
                                    ?? item.AllowedPackListTypeID
                                    ?? item.id
                                    ?? item.ID
                                    ?? ''
                                );
                            })
                            .filter((id: string) => id !== '')
                    );
                } else {
                    hasError = true;
                }

                setState(prev => ({
                    ...prev,
                    allowedPacklistTypesDropdownData: dropdownData,
                    allowedPacklistTypesSelected: selectedIds,
                    allowedPacklistTypesDlgLoading: false,
                    allowedPacklistTypesDlgError: hasError ? ((dataRes.callStatusMessage || dropdownRes.callStatusMessage || 'Error loading allowed packlist types')) : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    allowedPacklistTypesDlgLoading: false,
                    allowedPacklistTypesDlgError: 'Error loading allowed packlist types: ' + err.toString(),
                }));
            });
    };

    const closeAllowedPacklistTypesDialog = () => {
        setState(prev => ({
            ...prev,
            allowedPacklistTypesDlgOpen: false,
            allowedPacklistTypesMilestone: null,
            allowedPacklistTypesDropdownData: [],
            allowedPacklistTypesSelected: new Set<string>(),
            allowedPacklistTypesDlgLoading: false,
            allowedPacklistTypesDlgError: '',
            allowedPacklistTypesDlgFilterText: '',
        }));
    };

    const handleAllowedPacklistTypesFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, allowedPacklistTypesDlgFilterText: e.target.value }));
    };

    const toggleAllowedPacklistTypeSelection = (packlistTypeId: string) => {
        setState(prev => {
            const newSelected = new Set(prev.allowedPacklistTypesSelected);
            if (newSelected.has(packlistTypeId)) {
                newSelected.delete(packlistTypeId);
            } else {
                newSelected.add(packlistTypeId);
            }

            if (prev.allowedPacklistTypesMilestone) {
                const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMilestonePackListType';
                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                    body: JSON.stringify({
                        selected: prev.allowedPacklistTypesMilestone.milestoneID,
                        checked: Array.from(newSelected).map(id => parseInt(id, 10)).filter(id => Number.isFinite(id)),
                    }),
                })
                    .then(resp => resp.json())
                    .then(res => {
                        if (res.callStatus === 'UNAUTH') {
                            const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                            props.updateUserInfo(uInf);
                        } else if (res.callStatus !== 'OK') {
                            setState(s => ({ ...s, allowedPacklistTypesDlgError: 'Error saving: ' + (res.callStatusMessage || '') }));
                        }
                    })
                    .catch(err => {
                        setState(s => ({ ...s, allowedPacklistTypesDlgError: 'Error saving: ' + err.toString() }));
                    });
            }

            return { ...prev, allowedPacklistTypesSelected: newSelected };
        });
    };

    const openUserRolesDialog = (milestone: MilestoneRow) => {
        setState(prev => ({
            ...prev,
            userRolesDlgOpen: true,
            userRolesMilestone: milestone,
            userRolesDropdownData: [],
            userRolesData: [],
            userRolesDlgLoading: true,
            userRolesDlgError: '',
            userRolesEditingKey: null,
            userRolesOriginalItem: null,
            userRolesDeleteConfirmKey: null,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));

        const dataUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRoleMilestoneForDropDown?MilestoneID=${milestone.milestoneID}`;
        const dropdownUrl = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolesForDropDown`;

        Promise.all([
            fetch(dataUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
            fetch(dropdownUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json()),
        ])
            .then(([dataRes, dropdownRes]) => {
                if (dataRes.callStatus === 'UNAUTH' || dropdownRes.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, userRolesDlgLoading: false }));
                    return;
                }

                if (dataRes.callStatus !== 'OK' || !Array.isArray(dataRes.results)) {
                    setState(prev => ({
                        ...prev,
                        userRolesDlgLoading: false,
                        userRolesDlgError: dataRes.callStatusMessage || 'Failed to load user roles',
                    }));
                    return;
                }

                const dropdownData: LookupListItem[] = dropdownRes.callStatus === 'OK' && Array.isArray(dropdownRes.results)
                    ? dropdownRes.results.map((item: any) => ({
                        value: String(item.value ?? item.id ?? ''),
                        display: String(item.display ?? item.name ?? `User Role ${item.value ?? item.id ?? ''}`),
                    })).filter((item: LookupListItem) => item.value !== '')
                    : [];

                const rows: UserRoleMilestoneRow[] = dataRes.results.map((item: any) => {
                    const userRoleMilestoneId = String(item.userRoleMilestoneID ?? item.UserRoleMilestoneID ?? item.rowGUID ?? item.RowGUID ?? '');
                    const userRoleIdNumber = Number(item.userRoleID ?? item.UserRoleID ?? 0);
                    const resolvedDisplay = dropdownData.find(d => Number(d.value) === userRoleIdNumber)?.display
                        || item.display
                        || item.Display
                        || `User Role ${userRoleIdNumber}`;
                    return {
                        clientRowKey: userRoleMilestoneId || `ur-${userRoleIdNumber}-${Math.random().toString(36).slice(2, 8)}`,
                        UserRoleMilestoneID: userRoleMilestoneId,
                        MilestoneID: Number(item.milestoneID ?? item.MilestoneID ?? milestone.milestoneID),
                        UserRoleID: Number.isFinite(userRoleIdNumber) && userRoleIdNumber > 0 ? userRoleIdNumber : null,
                        CanScan: Boolean(item.canScan ?? item.CanScan ?? false),
                        LastChangedBy: String(item.lastChangedBy ?? item.LastChangedBy ?? ''),
                        LastChangedOn: (item.lastChangedOn ?? item.LastChangedOn) ? new Date(item.lastChangedOn ?? item.LastChangedOn) : null,
                        display: String(resolvedDisplay),
                        inEdit: false,
                    };
                });

                setState(prev => ({
                    ...prev,
                    userRolesDropdownData: dropdownData,
                    userRolesData: rows,
                    userRolesDlgLoading: false,
                    userRolesDlgError: dropdownData.length === 0 && (!dropdownRes || dropdownRes.callStatus !== 'OK')
                        ? (dropdownRes?.callStatusMessage || 'Failed to load user role dropdown')
                        : '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    userRolesDlgLoading: false,
                    userRolesDlgError: 'Error loading user roles: ' + err.toString(),
                }));
            });
    };

    const closeUserRolesDialog = () => {
        setState(prev => ({
            ...prev,
            userRolesDlgOpen: false,
            userRolesMilestone: null,
            userRolesDropdownData: [],
            userRolesData: [],
            userRolesDlgLoading: false,
            userRolesDlgError: '',
            userRolesEditingKey: null,
            userRolesOriginalItem: null,
            userRolesDeleteConfirmKey: null,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));
    };

    const openUserRoleMilestoneDeleteConfirm = (row: UserRoleMilestoneRow) => {
        setState(prev => ({
            ...prev,
            userRolesDeleteConfirmKey: row.clientRowKey,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));
    };

    const closeUserRoleMilestoneDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            userRolesDeleteConfirmKey: null,
            userRolesDeleteInProgress: false,
            userRolesDeleteError: '',
        }));
    };

    const confirmUserRoleMilestoneDelete = () => {
        const key = state.userRolesDeleteConfirmKey;
        if (!key) return;

        const row = state.userRolesData.find(r => r.clientRowKey === key);
        if (!row) {
            closeUserRoleMilestoneDeleteConfirm();
            return;
        }

        if (row.UserRoleMilestoneID === '') {
            setState(prev => ({
                ...prev,
                userRolesData: prev.userRolesData.filter(r => r.clientRowKey !== key),
                userRolesEditingKey: null,
                userRolesOriginalItem: null,
                userRolesDeleteConfirmKey: null,
                userRolesDeleteInProgress: false,
                userRolesDeleteError: '',
            }));
            return;
        }

        setState(prev => ({ ...prev, userRolesDeleteInProgress: true, userRolesDeleteError: '' }));
        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/DeleteUserRoleMilestone`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(row.UserRoleMilestoneID),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, userRolesDeleteInProgress: false }));
                    return;
                }
                if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, userRolesDeleteInProgress: false, userRolesDeleteError: 'Error deleting: ' + (res.callStatusMessage || '') }));
                    return;
                }

                setState(prev => ({
                    ...prev,
                    userRolesData: prev.userRolesData.filter(r => r.clientRowKey !== key),
                    userRolesDlgLoading: false,
                    userRolesEditingKey: null,
                    userRolesOriginalItem: null,
                    userRolesDeleteConfirmKey: null,
                    userRolesDeleteInProgress: false,
                    userRolesDeleteError: '',
                }));
            })
            .catch(err => {
                setState(prev => ({ ...prev, userRolesDeleteInProgress: false, userRolesDeleteError: 'Error deleting: ' + err.toString() }));
            });
    };

    const startEditUserRoleMilestone = (row: UserRoleMilestoneRow) => {
        if (state.userRolesEditingKey !== null) return;
        setState(prev => ({
            ...prev,
            userRolesEditingKey: row.clientRowKey,
            userRolesOriginalItem: { ...row },
            userRolesData: prev.userRolesData.map(r => ({ ...r, inEdit: r.clientRowKey === row.clientRowKey })),
            userRolesDlgError: '',
        }));
    };

    const addUserRoleMilestone = () => {
        if (!state.userRolesMilestone || state.userRolesEditingKey !== null) return;
        const key = `new-ur-${Date.now()}`;
        const row: UserRoleMilestoneRow = {
            clientRowKey: key,
            UserRoleMilestoneID: '',
            MilestoneID: state.userRolesMilestone.milestoneID,
            UserRoleID: null,
            CanScan: false,
            LastChangedBy: '',
            LastChangedOn: null,
            display: '',
            inEdit: true,
        };
        setState(prev => ({
            ...prev,
            userRolesData: [...prev.userRolesData, row],
            userRolesEditingKey: key,
            userRolesOriginalItem: null,
            userRolesDlgError: '',
        }));
    };

    const cancelEditUserRoleMilestone = (row: UserRoleMilestoneRow) => {
        setState(prev => {
            if (!prev.userRolesOriginalItem || row.UserRoleMilestoneID === '') {
                return {
                    ...prev,
                    userRolesData: prev.userRolesData
                        .filter(r => r.clientRowKey !== row.clientRowKey || r.UserRoleMilestoneID !== '')
                        .map(r => ({ ...r, inEdit: false })),
                    userRolesEditingKey: null,
                    userRolesOriginalItem: null,
                };
            }

            const original = prev.userRolesOriginalItem;
            return {
                ...prev,
                userRolesData: prev.userRolesData.map(r => r.clientRowKey === original.clientRowKey ? { ...original, inEdit: false } : { ...r, inEdit: false }),
                userRolesEditingKey: null,
                userRolesOriginalItem: null,
            };
        });
    };

    const saveUserRoleMilestone = (row: UserRoleMilestoneRow) => {
        if (!state.userRolesMilestone) return;
        if (!row.UserRoleID) {
            setState(prev => ({ ...prev, userRolesDlgError: 'Please select a User Role before saving.' }));
            return;
        }

        setState(prev => ({ ...prev, userRolesDlgLoading: true, userRolesDlgError: '' }));

        const payload = {
            UserRoleMilestoneID: row.UserRoleMilestoneID || '',
            MilestoneID: state.userRolesMilestone.milestoneID,
            UserRoleID: row.UserRoleID,
            CanScan: !!row.CanScan,
            LastChangedBy: row.LastChangedBy || props.userInf.displayName || '',
            LastChangedOn: row.LastChangedOn,
        };

        fetch(`${props.userInf.currProject.apiUrl}/api/TMI/AddOrUpdateUserRoleMilestone`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, userRolesDlgLoading: false }));
                    return;
                }
                if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, userRolesDlgLoading: false, userRolesDlgError: 'Error saving: ' + (res.callStatusMessage || '') }));
                    return;
                }

                const newId = String(res.stringResponse ?? res.StringResponse ?? row.UserRoleMilestoneID ?? '');
                setState(prev => ({
                    ...prev,
                    userRolesData: prev.userRolesData.map(r =>
                        r.clientRowKey === row.clientRowKey
                            ? {
                                ...r,
                                UserRoleMilestoneID: newId,
                                inEdit: false,
                                display: prev.userRolesDropdownData.find(d => Number(d.value) === r.UserRoleID)?.display || r.display,
                              }
                            : r
                    ),
                    userRolesDlgLoading: false,
                    userRolesEditingKey: null,
                    userRolesOriginalItem: null,
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    userRolesDlgLoading: false,
                    userRolesDlgError: 'Error saving: ' + err.toString(),
                }));
            });
    };

    const startEditMilestoneLine = (line: MilestoneLineRow) => {
        if (state.milestoneLinesEditingId !== null) return;
        setState(prev => ({
            ...prev,
            milestoneLinesEditingId: line.MilestoneLineID,
            milestoneLinesOriginalItem: { ...line },
            milestoneLinesData: prev.milestoneLinesData.map(r => ({ ...r, inEdit: r.MilestoneLineID === line.MilestoneLineID })),
        }));
    };

    const addMilestoneLine = () => {
        if (!state.milestoneLinesMilestone || state.milestoneLinesEditingId !== null) return;
        const tempId = -(Date.now());
        const newLine: MilestoneLineRow = {
            MilestoneLineID: tempId,
            MilestoneID: state.milestoneLinesMilestone.milestoneID,
            MilestoneLineDesc: '',
            ResponseRequired: false,
            AttributeClassID: 0,
            PDAIsVisible: false,
            WebIsVisible: false,
            PDACanWrite: false,
            WebCanWrite: false,
            LastChangedBy: '',
            LastChangedOn: null,
            inEdit: true,
        };

        setState(prev => ({
            ...prev,
            milestoneLinesData: [newLine, ...prev.milestoneLinesData.map(r => ({ ...r, inEdit: false }))],
            milestoneLinesEditingId: tempId,
            milestoneLinesOriginalItem: null,
            milestoneLinesSelectedId: tempId,
        }));
    };

    const cancelEditMilestoneLine = (line: MilestoneLineRow) => {
        setState(prev => {
            if (line.MilestoneLineID < 0) {
                return {
                    ...prev,
                    milestoneLinesData: prev.milestoneLinesData.filter(r => r.MilestoneLineID !== line.MilestoneLineID).map(r => ({ ...r, inEdit: false })),
                    milestoneLinesEditingId: null,
                    milestoneLinesOriginalItem: null,
                    milestoneLinesSelectedId: null,
                };
            }

            if (!prev.milestoneLinesOriginalItem) {
                return {
                    ...prev,
                    milestoneLinesData: prev.milestoneLinesData.map(r => ({ ...r, inEdit: false })),
                    milestoneLinesEditingId: null,
                };
            }

            const original = prev.milestoneLinesOriginalItem;
            return {
                ...prev,
                milestoneLinesData: prev.milestoneLinesData.map(r => r.MilestoneLineID === original.MilestoneLineID ? { ...original, inEdit: false } : { ...r, inEdit: false }),
                milestoneLinesEditingId: null,
                milestoneLinesOriginalItem: null,
            };
        });
    };

    const saveMilestoneLine = (line: MilestoneLineRow) => {
        if (!state.milestoneLinesMilestone) return;
        if (!line.MilestoneLineDesc.trim()) {
            setState(prev => ({ ...prev, milestoneLinesDlgError: 'Milestone Line Description is required.' }));
            return;
        }

        setState(prev => ({ ...prev, milestoneLinesDlgError: '' }));

        const url = props.userInf.currProject.apiUrl + '/api/TMI/AddOrUpdateMilestoneLine';
        const payload = {
            MilestoneLineID: line.MilestoneLineID > 0 ? line.MilestoneLineID : 0,
            MilestoneID: state.milestoneLinesMilestone.milestoneID,
            MilestoneLineDesc: line.MilestoneLineDesc,
            AttributeClassID: Number(line.AttributeClassID || 0),
            ResponseRequired: !!line.ResponseRequired,
            PDAIsVisible: !!line.PDAIsVisible,
            WebIsVisible: !!line.WebIsVisible,
            PDACanWrite: !!line.PDACanWrite,
            WebCanWrite: !!line.WebCanWrite,
        };

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(payload),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    return;
                }
                if (res.callStatus !== 'OK') {
                    setState(prev => ({ ...prev, milestoneLinesDlgError: 'Error saving milestone line: ' + (res.callStatusMessage || '') }));
                    return;
                }

                if (!state.milestoneLinesMilestone) return;
                const reloadUrl = props.userInf.currProject.apiUrl + '/api/TMI/GetMilestoneLines?MilestoneID=' + state.milestoneLinesMilestone.milestoneID;
                fetch(reloadUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                })
                    .then(r => r.json())
                    .then(dataRes => {
                        if (dataRes.callStatus === 'OK') {
                            setState(prev => ({
                                ...prev,
                                milestoneLinesData: Array.isArray(dataRes.results) ? dataRes.results.map((r: any) => normalizeMilestoneLine(r)) : [],
                                milestoneLinesEditingId: null,
                                milestoneLinesOriginalItem: null,
                            }));
                        } else {
                            setState(prev => ({ ...prev, milestoneLinesDlgError: 'Saved, but failed to refresh milestone lines.' }));
                        }
                    })
                    .catch(() => {
                        setState(prev => ({ ...prev, milestoneLinesDlgError: 'Saved, but failed to refresh milestone lines.' }));
                    });
            })
            .catch(err => {
                setState(prev => ({ ...prev, milestoneLinesDlgError: 'Error saving milestone line: ' + err.toString() }));
            });
    };

    const openMilestoneLineDeleteConfirm = (lineId: number) => {
        setState(prev => ({ ...prev, milestoneLinesDeleteConfirmId: lineId, milestoneLinesDeleteError: '' }));
    };

    const closeMilestoneLineDeleteConfirm = () => {
        setState(prev => ({
            ...prev,
            milestoneLinesDeleteConfirmId: null,
            milestoneLinesDeleteInProgress: false,
            milestoneLinesDeleteError: '',
        }));
    };

    const confirmMilestoneLineDelete = () => {
        if (state.milestoneLinesDeleteConfirmId === null || !state.milestoneLinesMilestone) return;
        setState(prev => ({ ...prev, milestoneLinesDeleteInProgress: true, milestoneLinesDeleteError: '' }));

        const lineId = state.milestoneLinesDeleteConfirmId;
        const url = props.userInf.currProject.apiUrl + '/api/TMI/DeleteMilestoneLine';
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify(lineId),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, milestoneLinesDeleteInProgress: false }));
                    return;
                }
                if (res.callStatus !== 'OK') {
                    setState(prev => ({
                        ...prev,
                        milestoneLinesDeleteInProgress: false,
                        milestoneLinesDeleteError: 'Delete failed: ' + (res.callStatusMessage || ''),
                    }));
                    return;
                }

                const reloadUrl = props.userInf.currProject.apiUrl + '/api/TMI/GetMilestoneLines?MilestoneID=' + state.milestoneLinesMilestone!.milestoneID;
                fetch(reloadUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                })
                    .then(r => r.json())
                    .then(dataRes => {
                        if (dataRes.callStatus === 'OK') {
                            setState(prev => ({
                                ...prev,
                                milestoneLinesData: Array.isArray(dataRes.results) ? dataRes.results.map((r: any) => normalizeMilestoneLine(r)) : [],
                                milestoneLinesDeleteConfirmId: null,
                                milestoneLinesDeleteInProgress: false,
                                milestoneLinesDeleteError: '',
                                milestoneLinesSelectedId: null,
                            }));
                        } else {
                            setState(prev => ({
                                ...prev,
                                milestoneLinesDeleteInProgress: false,
                                milestoneLinesDeleteError: 'Deleted, but failed to refresh milestone lines.',
                            }));
                        }
                    })
                    .catch(() => {
                        setState(prev => ({
                            ...prev,
                            milestoneLinesDeleteInProgress: false,
                            milestoneLinesDeleteError: 'Deleted, but failed to refresh milestone lines.',
                        }));
                    });
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    milestoneLinesDeleteInProgress: false,
                    milestoneLinesDeleteError: 'Delete failed: ' + err.toString(),
                }));
            });
    };

    // Shared editable cells for the Milestone Lines dialog grid. Previously each GridColumn below
    // defined its own inline `cell={(props) => {...}}` arrow function directly in JSX, recreated
    // on every render (e.g. every keystroke in the Description input), which made Kendo's Grid
    // remount the <input>/<select> and drop focus after a single character. These are memoized
    // with useCallback (reading live values via stateRef.current) so each GridColumn's `cell` prop
    // keeps a stable function identity across renders.
    const milestoneLineTextCell = useCallback((props: GridCellProps) => {
        const isSelected = stateRef.current.milestoneLinesSelectedId === props.dataItem.MilestoneLineID;
        return (
            <td className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                {props.dataItem.inEdit ? (
                    <input
                        type="text"
                        className="k-input k-input-sm"
                        value={props.dataItem.MilestoneLineDesc || ''}
                        onChange={(e) => {
                            const val = e.target.value;
                            setState(prev => ({
                                ...prev,
                                milestoneLinesData: prev.milestoneLinesData.map(r => r.MilestoneLineID === props.dataItem.MilestoneLineID ? { ...r, MilestoneLineDesc: val } : r),
                            }));
                        }}
                        style={{ width: '100%' }}
                    />
                ) : (
                    props.dataItem.MilestoneLineDesc
                )}
            </td>
        );
    }, []);

    const milestoneLineAttributeClassCell = useCallback((props: GridCellProps) => {
        const isSelected = stateRef.current.milestoneLinesSelectedId === props.dataItem.MilestoneLineID;
        const display = stateRef.current.milestoneLinesAttributeClassesDropdown.find(item => String(item.value) === String(props.dataItem.AttributeClassID))?.display || props.dataItem.AttributeClassID;
        return (
            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                {props.dataItem.inEdit ? (
                    <select
                        className="k-input k-input-sm"
                        value={String(props.dataItem.AttributeClassID ?? 0)}
                        onChange={(e) => {
                            const val = parseInt(e.target.value || '0', 10);
                            setState(prev => ({
                                ...prev,
                                milestoneLinesData: prev.milestoneLinesData.map(r => r.MilestoneLineID === props.dataItem.MilestoneLineID ? { ...r, AttributeClassID: Number.isFinite(val) ? val : 0 } : r),
                            }));
                        }}
                        style={{ width: '100%' }}
                    >
                        <option value="0">-- Select --</option>
                        {stateRef.current.milestoneLinesAttributeClassesDropdown.map(item => (
                            <option key={item.value} value={item.value}>{item.display}</option>
                        ))}
                    </select>
                ) : (
                    display
                )}
            </td>
        );
    }, []);

    // Shared boolean checkbox cell reused across the Response Required/PDA Visible/Web Visible/
    // PDA Write/Web Write columns, which previously were five near-identical inline cell
    // renderers differing only by field name. Takes the field name via closure in a small factory
    // so each GridColumn still gets a dedicated, stably-identified cell function.
    const createMilestoneLineBooleanCell = useCallback((field: keyof MilestoneLineRow) => (props: GridCellProps) => {
        const isSelected = stateRef.current.milestoneLinesSelectedId === props.dataItem.MilestoneLineID;
        const val = props.dataItem[field];
        return (
            <td className={`tmi-dialog-grid-center-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}>
                {props.dataItem.inEdit ? (
                    <input
                        type="checkbox"
                        checked={!!val}
                        onChange={(e) => {
                            const checked = e.target.checked;
                            setState(prev => ({
                                ...prev,
                                milestoneLinesData: prev.milestoneLinesData.map(r => r.MilestoneLineID === props.dataItem.MilestoneLineID ? { ...r, [field]: checked } : r),
                            }));
                        }}
                    />
                ) : (val ? '✓' : '')}
            </td>
        );
    }, []);

    const milestoneLineResponseRequiredCellRef = useRef<(props: GridCellProps) => JSX.Element>();
    if (!milestoneLineResponseRequiredCellRef.current) milestoneLineResponseRequiredCellRef.current = createMilestoneLineBooleanCell('ResponseRequired');
    const milestoneLinePDAIsVisibleCellRef = useRef<(props: GridCellProps) => JSX.Element>();
    if (!milestoneLinePDAIsVisibleCellRef.current) milestoneLinePDAIsVisibleCellRef.current = createMilestoneLineBooleanCell('PDAIsVisible');
    const milestoneLineWebIsVisibleCellRef = useRef<(props: GridCellProps) => JSX.Element>();
    if (!milestoneLineWebIsVisibleCellRef.current) milestoneLineWebIsVisibleCellRef.current = createMilestoneLineBooleanCell('WebIsVisible');
    const milestoneLinePDACanWriteCellRef = useRef<(props: GridCellProps) => JSX.Element>();
    if (!milestoneLinePDACanWriteCellRef.current) milestoneLinePDACanWriteCellRef.current = createMilestoneLineBooleanCell('PDACanWrite');
    const milestoneLineWebCanWriteCellRef = useRef<(props: GridCellProps) => JSX.Element>();
    if (!milestoneLineWebCanWriteCellRef.current) milestoneLineWebCanWriteCellRef.current = createMilestoneLineBooleanCell('WebCanWrite');

    const handleMenuItemClick = (action: string) => {
        const item = state.menuDataItem;
        if (!item) return;

        switch (action) {
            case 'milestoneLines':
                openMilestoneLinesDialog(item);
                break;
            case 'allowedItemTypes':
                openAllowedItemTypesDialog(item);
                break;
            case 'allowedPacklistTypes':
                openAllowedPacklistTypesDialog(item);
                break;
            case 'userRoles':
                openUserRolesDialog(item);
                break;
        }

        closeRowMenu();
    };

    useEffect(() => {
        if (!state.menuAnchor || state.menuPosition !== null) return;
        const el = menuRef.current;
        if (!el) return;
        const timer = setTimeout(() => {
            try {
                const menuRect = el.getBoundingClientRect();
                const snap = state.menuAnchorRect;
                const actualAnchorRect = snap ? snap : (state.menuAnchor ? state.menuAnchor.getBoundingClientRect() as any : { left: 0, right: 0, top: 0, bottom: 0, width: 0, height: 0 });
                const scrollX = window.scrollX || window.pageXOffset || 0;
                const scrollY = window.scrollY || window.pageYOffset || 0;
                const winW = window.innerWidth;
                const winH = window.innerHeight;
                const menuW = Math.round(menuRect.width);
                const menuH = Math.round(menuRect.height);

                let left = Math.round(actualAnchorRect.left + scrollX);
                let top = Math.round(actualAnchorRect.bottom + scrollY);

                if (left + menuW > scrollX + winW) left = Math.round(scrollX + Math.max(8, winW - menuW - 8));
                if (left < scrollX + 8) left = Math.round(scrollX + 8);

                if (top + menuH > scrollY + winH) {
                    const flipped = Math.round(actualAnchorRect.top + scrollY - menuH + 2);
                    if (flipped >= scrollY + 8) top = flipped; else top = Math.round(scrollY + Math.max(8, winH - menuH - 8));
                }

                setState(prev => ({ ...prev, menuPosition: { left, top } }));
            } catch {
                // ignore measurement errors
            }
        }, 10);

        return () => clearTimeout(timer);
    }, [state.menuAnchor, state.menuPosition, state.menuAnchorRect]);

    useEffect(() => {
        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (menuRef.current && !menuRef.current.contains(target) && state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        if (state.menuDataItem) {
            document.addEventListener('click', handleDocumentClick);
            return () => document.removeEventListener('click', handleDocumentClick);
        }
    }, [state.menuDataItem, state.menuAnchor]);

    // Shared editable text cell reused across the Description/Code/Order/Alt Description/
    // Weighted %/Days In Future/Changed By columns. Memoized with useCallback (reading live
    // values via stateRef.current instead of closing over `state` directly) so the GridColumn's
    // `cell` prop keeps a stable function identity across renders. Previously this was a plain
    // function recreated on every render, so Kendo's Grid saw a "new" cell renderer and remounted
    // the <input> on every keystroke, dropping focus after a single character.
    const textCell = useCallback((props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const isEditing = stateRef.current.editRowKey === props.dataItem.clientRowKey;
        const editCellId = `${props.dataItem.clientRowKey}::${props.field!}`;
        return (
            <td className="tmi-dialog-grid-text-cell" title={String(val)}>
                {isEditing ? (
                    <input
                        type="text"
                        className="k-input k-input-md"
                        data-edit-cell={editCellId}
                        value={String(val)}
                        onChange={(e) => {
                            const target = e.target as HTMLInputElement;
                            const selectionStart = target.selectionStart;
                            const selectionEnd = target.selectionEnd;
                            const updated = { ...props.dataItem, [props.field!]: e.target.value };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                            keepEditInputFocus(updated.clientRowKey, props.field!, selectionStart, selectionEnd);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                ) : val}
            </td>
        );
    }, [keepEditInputFocus]);

    const dateCell = useCallback((props: GridCellProps) => {
        const val = props.dataItem[props.field!];
        const display = val ? new Date(val).toLocaleString() : '';
        return <td className="tmi-dialog-grid-text-cell" title={display}>{display}</td>;
    }, []);

    const BooleanCell = useCallback((props: GridCellProps) => {
        const val = props.dataItem[props.field!];
        const isEditing = stateRef.current.editRowKey === props.dataItem.clientRowKey;
        return (
            <td className="tmi-dialog-grid-center-cell" style={{ cursor: 'pointer' }}>
                {isEditing ? (
                    <Checkbox
                        checked={Boolean(val)}
                        onChange={(e: any) => {
                            const updated = { ...props.dataItem, [props.field!]: Boolean(e.value) };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                        }}
                    />
                ) : (val ? '✓' : '')}
            </td>
        );
    }, []);

    const MilestoneTypeDropdownCell = useCallback((props: GridCellProps) => {
        const val = props.dataItem[props.field!] ?? '';
        const isEditing = stateRef.current.editRowKey === props.dataItem.clientRowKey;

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={String(val ?? '')}
                        onChange={(e) => {
                            const updated = { ...props.dataItem, milestoneTypeID: e.target.value || null };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {stateRef.current.milestoneTypesDropdown.map(mt => (
                            <option key={mt.value} value={mt.value}>{mt.display}</option>
                        ))}
                    </select>
                </td>
            );
        }

        const display = stateRef.current.milestoneTypesDropdown.find(mt => mt.value === String(val ?? ''))?.display || val;
        return <td className="tmi-dialog-grid-text-cell" title={String(display)}>{display}</td>;
    }, []);

    const BackfillMilestoneDropdownCell = useCallback((props: GridCellProps) => {
        const val = props.dataItem.backfillFromMilestoneID ?? '';
        const isEditing = stateRef.current.editRowKey === props.dataItem.clientRowKey;

        if (isEditing) {
            return (
                <td className="tmi-dialog-grid-text-cell" style={{ padding: '2px' }}>
                    <select
                        className="k-input k-input-md"
                        value={String(val ?? '')}
                        onChange={(e) => {
                            const updated = { ...props.dataItem, backfillFromMilestoneID: e.target.value ? Number(e.target.value) : null };
                            setState(prev => ({ ...prev, data: prev.data.map(item => item.clientRowKey === updated.clientRowKey ? updated : item) }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    >
                        <option value="">-- Select --</option>
                        {stateRef.current.backfillMilestonesDropdown.map(m => (
                            <option key={m.value} value={m.value}>{m.display}</option>
                        ))}
                    </select>
                </td>
            );
        }

        const display = stateRef.current.backfillMilestonesDropdown.find(m => String(m.value) === String(val ?? ''))?.display || val;
        return <td className="tmi-dialog-grid-text-cell" title={String(display)}>{display}</td>;
    }, []);

    const HamburgerHeaderCell = () => (
        <th className="tmi-header-action-cell tmi-command-column">
            <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={fetchData} disabled={state.loading} title="Refresh">
                <span className="k-icon k-font-icon k-i-reload" />
            </button>
        </th>
    );

    const HamburgerCell = (props: GridCellProps) => (
        <td style={{ textAlign: 'center', padding: '2px' }}>
            {state.editRowKey === props.dataItem.clientRowKey ? (
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-success"
                    title="Save"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleSave(props.dataItem as MilestoneRow);
                    }}
                >
                    <span className="k-icon k-font-icon k-i-check" />
                </button>
            ) : (
                <button
                    className="k-button k-button-sm k-rounded-md"
                    title="Actions"
                    onClick={(e) => {
                        e.stopPropagation();
                        openRowMenu(props.dataItem as MilestoneRow, e.currentTarget as HTMLElement);
                    }}
                >
                    <span className="k-icon k-font-icon k-i-more-vertical" />
                </button>
            )}
        </td>
    );

    const CommandCell = (props: GridCellProps) => {
        const { dataItem } = props;
        const inEdit = state.editRowKey === dataItem.clientRowKey;
        return (
            <td className="packlists-command-cell tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleCancel(); }} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { openDeleteConfirm(dataItem as MilestoneRow); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleEnterEdit(dataItem as MilestoneRow); }} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { openDeleteConfirm(dataItem as MilestoneRow); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    };

    const CommandHeaderCell = () => (
        <th className="tmi-command-header-cell tmi-command-column">
            <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" title={state.showSecondaryColumns ? 'Show Primary Columns' : 'Show Secondary Columns'} onClick={toggleColumnVisibility}>
                <span className={`k-icon k-font-icon ${state.showSecondaryColumns ? 'k-i-arrow-chevron-left' : 'k-i-arrow-chevron-right'}`} />
            </button>
            <button className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-action-button tmi-dialog-action-button-next" title="New Milestone" onClick={openNewDialog}>
                <span className="k-icon k-font-icon k-i-plus" />
            </button>
        </th>
    );

    const buildHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `v2_text_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field)
                    ? (stateRef.current.sort[0].dir as string)
                    : undefined;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                        <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                            </label>
                            <input
                                type="text"
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            />
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort, handleHeaderFocus, handleHeaderBlur, handleHeaderFilterChange]);

    const buildBooleanHeaderCellRenderer = useCallback((field: string, title: string) => {
        const cacheKey = `v2_bool_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const val = stateRef.current.filterTexts?.[field] ?? '';
                const focused = stateRef.current.headerFocusField === field;
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" aria-hidden="true" />
                        <div className={`tmi-select-floating ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                            <label onClick={() => toggleSort(field)} style={{ whiteSpace: 'normal', wordWrap: 'break-word', overflow: 'visible', cursor: 'pointer', userSelect: 'none', pointerEvents: 'auto', position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span>{title}</span>
                                <span className="tmi-sort-glyph-inline">↕</span>
                            </label>
                            <select
                                className="k-input k-input-sm"
                                value={val}
                                onChange={(e) => handleHeaderFilterChange(field, e as unknown as React.ChangeEvent<HTMLInputElement>)}
                                onFocus={() => handleHeaderFocus(field)}
                                onBlur={() => handleHeaderBlur()}
                                onMouseDown={(e) => { e.stopPropagation(); }}
                            >
                                <option value="">All</option>
                                <option value="true">Yes</option>
                                <option value="false">No</option>
                            </select>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [handleHeaderFilterChange, handleHeaderFocus, handleHeaderBlur, toggleSort]);

    const createSimpleHeaderCell = useCallback((field: string, title: string) => {
        const cacheKey = `v2_simple_${field}`;
        if (!headerCellRenderersRef.current[cacheKey]) {
            headerCellRenderersRef.current[cacheKey] = (props: any) => {
                const sortDir = (stateRef.current.sort && stateRef.current.sort.length > 0 && stateRef.current.sort[0].field === field)
                    ? (stateRef.current.sort[0].dir as string)
                    : undefined;
                const onSimpleHeaderMouseDown = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleSort(field);
                };
                const blockSimpleHeaderClick = (e: React.MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    e.stopPropagation();
                };
                return (
                    <th className="tmi-header-filter">
                        <div className="k-link" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick} aria-hidden="true" />
                        <div
                            onMouseDown={onSimpleHeaderMouseDown}
                            onClick={blockSimpleHeaderClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                padding: '4px 6px',
                                boxSizing: 'border-box',
                                fontSize: '12px',
                                lineHeight: '1.3',
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                                flex: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                        >
                            <span onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{title}</span>
                            <span className="tmi-sort-glyph-inline" onMouseDown={onSimpleHeaderMouseDown} onClick={blockSimpleHeaderClick}>{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </div>
                    </th>
                );
            };
        }
        return headerCellRenderersRef.current[cacheKey];
    }, [toggleSort]);

    const filters = state.filterTexts || {};
    const filteredData = Object.keys(filters).length === 0 || Object.values(filters).every(v => !v)
        ? state.data
        : state.data.filter(item => {
            return Object.entries(filters).every(([f, q]) => {
                if (!q) return true;
                const val = (item as any)[f];
                const ql = q.trim().toLowerCase();
                if (ql === 'true' || ql === 'false') {
                    return String(val ?? '').toLowerCase() === ql;
                }
                return String(val ?? '').toLowerCase().includes(ql);
            });
        });

    const sortedData = orderBy(filteredData, state.sort);

    const gridData = sortedData.map(item => ({
        ...item,
        [EDIT_FIELD]: state.editRowKey === item.clientRowKey,
    }));

    stateRef.current = state;

    return (
        <div className="packlists-container">
            {state.errorMsg && <div className="packlists-error">{state.errorMsg}</div>}

            <Grid
                className="tmi-grid PacklistTypes-grid"
                style={{ flex: 1, minHeight: 0 }}
                data={gridData}
                dataItemKey="clientRowKey"
                resizable
                editField={EDIT_FIELD}
                onItemChange={handleItemChange}
                onFilterChange={handleFilterChange}
            >
                <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                <GridColumn field="milestoneID" title="ID" width={60} hidden={!isColumnVisible('milestoneID', state.showSecondaryColumns)} headerCell={createSimpleHeaderCell('milestoneID', 'ID')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="milestoneDesc" title="Description" hidden={!isColumnVisible('milestoneDesc', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('milestoneDesc', 'Description')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="milestoneCode" title="Code" hidden={!isColumnVisible('milestoneCode', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('milestoneCode', 'Code')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="milestoneOrder" title="Order" width={70} hidden={!isColumnVisible('milestoneOrder', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('milestoneOrder', 'Order')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="milestoneAltDesc" title="Alt Description" hidden={!isColumnVisible('milestoneAltDesc', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('milestoneAltDesc', 'Alt Description')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="weightedPercentage" title="Weighted %" hidden={!isColumnVisible('weightedPercentage', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('weightedPercentage', 'Weighted %')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="daysInFutureAllowed" title="Days In Future" hidden={!isColumnVisible('daysInFutureAllowed', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('daysInFutureAllowed', 'Days In Future')} cell={textCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="milestoneTypeID" title="Milestone Type" hidden={!isColumnVisible('milestoneTypeID', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('milestoneTypeID', 'Milestone Type')} cell={MilestoneTypeDropdownCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="backfillFromMilestoneID" title="Backfill" hidden={!isColumnVisible('backfillFromMilestoneID', state.showSecondaryColumns)} headerCell={buildHeaderCellRenderer('backfillFromMilestoneID', 'Backfill')} cell={BackfillMilestoneDropdownCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="alwaysEditMilestoneLines" title="Always Edit Lines" width={90} hidden={!isColumnVisible('alwaysEditMilestoneLines', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('alwaysEditMilestoneLines', 'Always Edit Lines')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="isStocktake" title="Stocktake" width={90} hidden={!isColumnVisible('isStocktake', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('isStocktake', 'Stocktake')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="allowMultiScan" title="Multi Scan" width={90} hidden={!isColumnVisible('allowMultiScan', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('allowMultiScan', 'Multi Scan')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="doNotCascadeScan" title="No Cascade" width={90} hidden={!isColumnVisible('doNotCascadeScan', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('doNotCascadeScan', 'No Cascade')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="fillFromParts" title="Fill Parts" width={90} hidden={!isColumnVisible('fillFromParts', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('fillFromParts', 'Fill Parts')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="fillItemsFromPackLists" title="Fill Items" width={90} hidden={!isColumnVisible('fillItemsFromPackLists', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('fillItemsFromPackLists', 'Fill Items')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="partialChildrenWeight" title="Partial Child Weight" width={90} hidden={!isColumnVisible('partialChildrenWeight', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('partialChildrenWeight', 'Partial Child Weight')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="rollUpItemsToPackLists" title="Roll Up Items" width={90} hidden={!isColumnVisible('rollUpItemsToPackLists', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('rollUpItemsToPackLists', 'Roll Up Items')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="rollUpPackLists" title="Roll Up Packlists" width={90} hidden={!isColumnVisible('rollUpPackLists', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('rollUpPackLists', 'Roll Up Packlists')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="includeTime" title="Include Time" width={90} hidden={!isColumnVisible('includeTime', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('includeTime', 'Include Time')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="packListItemScan" title="Pack List Item Scan" width={90} hidden={!isColumnVisible('packListItemScan', state.showSecondaryColumns)} headerCell={buildBooleanHeaderCellRenderer('packListItemScan', 'Pack List Item Scan')} cell={BooleanCell} headerClassName="wrap-header" resizable={true} />
                <GridColumn field="lastChangedBy" title="Changed By" resizable={true} hidden={!isColumnVisible('lastChangedBy', state.showSecondaryColumns)} headerCell={createSimpleHeaderCell('lastChangedBy', 'Changed By')} cell={textCell} headerClassName="wrap-header" />
                <GridColumn field="lastChangedOn" title="Changed On" resizable={true} hidden={!isColumnVisible('lastChangedOn', state.showSecondaryColumns)} headerCell={createSimpleHeaderCell('lastChangedOn', 'Changed On')} cell={dateCell} headerClassName="wrap-header" />
                <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
            </Grid>

            {state.deleteConfirmItem && (
                <Dialog title="Confirm Delete" onClose={closeDeleteConfirm}>
                    <p>Delete Milestone '{state.deleteConfirmItem.milestoneCode}'?</p>
                    {state.deleteErrorMsg && <div className="packlists-error">{state.deleteErrorMsg}</div>}
                    <DialogActionsBar>
                        <button className="k-button k-button-solid-error" onClick={confirmDelete} disabled={state.deleteInProgress}>Delete</button>
                        <button className="k-button k-button-solid-base" onClick={closeDeleteConfirm} disabled={state.deleteInProgress}>Cancel</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.newDialogOpen && (
                <Dialog title="New Milestone" onClose={closeNewDialog}>
                    <div style={{ padding: '16px', maxHeight: '65vh', overflowY: 'auto' }}>
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px', color: '#4a4a4a' }}>Identity</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label>Milestone ID *</label>
                                    <input
                                        autoFocus
                                        type="number"
                                        className="k-input k-input-md"
                                        value={state.newDialogMilestoneID}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogMilestoneID: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Code</label>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogCode}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogCode: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogDesc}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogDesc: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Alt Description</label>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogAltDesc}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogAltDesc: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px', color: '#4a4a4a' }}>Numeric Values</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <label>Milestone Order *</label>
                                    <input
                                        type="number"
                                        className="k-input k-input-md"
                                        value={state.newDialogMilestoneOrder}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogMilestoneOrder: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Weighted %</label>
                                    <input
                                        type="number"
                                        className="k-input k-input-md"
                                        value={state.newDialogWeightedPercentage}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogWeightedPercentage: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Days In Future Allowed</label>
                                    <input
                                        type="number"
                                        className="k-input k-input-md"
                                        value={state.newDialogDaysInFutureAllowed}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogDaysInFutureAllowed: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    />
                                </div>
                                <div>
                                    <label>Milestone Type ID</label>
                                    <select
                                        className="k-input k-input-md"
                                        value={state.newDialogMilestoneTypeID}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogMilestoneTypeID: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    >
                                        <option value="">-- Select --</option>
                                        {state.milestoneTypesDropdown.map(mt => (
                                            <option key={mt.value} value={mt.value}>{mt.display}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label>Backfill From Milestone</label>
                                    <select
                                        className="k-input k-input-md"
                                        value={state.newDialogBackfillFromMilestoneID}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogBackfillFromMilestoneID: e.target.value }))}
                                        style={{ width: '100%', marginTop: '4px' }}
                                    >
                                        <option value="">-- Select --</option>
                                        {state.backfillMilestonesDropdown.map(m => (
                                            <option key={m.value} value={m.value}>{m.display}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', marginBottom: '10px', color: '#4a4a4a' }}>Options</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                                <Checkbox checked={state.newDialogAlwaysEditMilestoneLines} onChange={(e: any) => setState(prev => ({ ...prev, newDialogAlwaysEditMilestoneLines: Boolean(e.value) }))} label="Always Edit Milestone Lines" />
                                <Checkbox checked={state.newDialogIsStocktake} onChange={(e: any) => setState(prev => ({ ...prev, newDialogIsStocktake: Boolean(e.value) }))} label="Is Stocktake" />
                                <Checkbox checked={state.newDialogAllowMultiScan} onChange={(e: any) => setState(prev => ({ ...prev, newDialogAllowMultiScan: Boolean(e.value) }))} label="Allow Multi Scan" />
                                <Checkbox checked={state.newDialogDoNotCascadeScan} onChange={(e: any) => setState(prev => ({ ...prev, newDialogDoNotCascadeScan: Boolean(e.value) }))} label="Do Not Cascade Scan" />
                                <Checkbox checked={state.newDialogFillFromParts} onChange={(e: any) => setState(prev => ({ ...prev, newDialogFillFromParts: Boolean(e.value) }))} label="Fill From Parts" />
                                <Checkbox checked={state.newDialogFillItemsFromPackLists} onChange={(e: any) => setState(prev => ({ ...prev, newDialogFillItemsFromPackLists: Boolean(e.value) }))} label="Fill Items From PackLists" />
                                <Checkbox checked={state.newDialogPartialChildrenWeight} onChange={(e: any) => setState(prev => ({ ...prev, newDialogPartialChildrenWeight: Boolean(e.value) }))} label="Partial Children Weight" />
                                <Checkbox checked={state.newDialogRollUpItemsToPackLists} onChange={(e: any) => setState(prev => ({ ...prev, newDialogRollUpItemsToPackLists: Boolean(e.value) }))} label="Roll Up Items To PackLists" />
                                <Checkbox checked={state.newDialogRollUpPackLists} onChange={(e: any) => setState(prev => ({ ...prev, newDialogRollUpPackLists: Boolean(e.value) }))} label="Roll Up PackLists" />
                                <Checkbox checked={state.newDialogIncludeTime} onChange={(e: any) => setState(prev => ({ ...prev, newDialogIncludeTime: Boolean(e.value) }))} label="Include Time" />
                                <Checkbox checked={state.newDialogPackListItemScan} onChange={(e: any) => setState(prev => ({ ...prev, newDialogPackListItemScan: Boolean(e.value) }))} label="PackList Item Scan" />
                            </div>
                        </div>

                        {state.newDialogError && <div className="packlists-error">{state.newDialogError}</div>}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-solid-primary" onClick={submitNew} disabled={state.newDialogSaving}>Create</button>
                        <button className="k-button k-button-solid-base" onClick={closeNewDialog} disabled={state.newDialogSaving}>Cancel</button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.milestoneLinesDlgOpen && state.milestoneLinesMilestone && (
                <Dialog
                    title={`Milestone Lines For ${state.milestoneLinesMilestone.milestoneDesc || state.milestoneLinesMilestone.milestoneCode || `ID ${state.milestoneLinesMilestone.milestoneID}`}`}
                    onClose={closeMilestoneLinesDialog}
                    width={1200}
                    height={620}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '480px' }}>
                        {state.milestoneLinesDlgError && (
                            <div style={{ color: '#d32f2f', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                                {state.milestoneLinesDlgError}
                            </div>
                        )}
                        {state.milestoneLinesDlgLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                            </div>
                        ) : (
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <Grid
                                    style={{ flex: 1 }}
                                    data={orderBy(state.milestoneLinesData, [{ field: 'MilestoneLineID', dir: 'asc' }])}
                                    className="AllowedPacklistTypes-grid"
                                    dataItemKey="MilestoneLineID"
                                    onRowClick={(e) => setState(prev => ({ ...prev, milestoneLinesSelectedId: e.dataItem.MilestoneLineID }))}
                                >
                                    <GridColumn
                                        field="MilestoneLineDesc"
                                        title="Description"
                                        cell={milestoneLineTextCell}
                                    />
                                    <GridColumn
                                        field="AttributeClassID"
                                        title="Attribute Class ID"
                                        width={150}
                                        cell={milestoneLineAttributeClassCell}
                                    />
                                    <GridColumn
                                        field="ResponseRequired"
                                        title="Response Required"
                                        width={150}
                                        cell={milestoneLineResponseRequiredCellRef.current}
                                    />
                                    <GridColumn
                                        field="PDAIsVisible"
                                        title="PDA Visible"
                                        width={120}
                                        cell={milestoneLinePDAIsVisibleCellRef.current}
                                    />
                                    <GridColumn
                                        field="WebIsVisible"
                                        title="Web Visible"
                                        width={120}
                                        cell={milestoneLineWebIsVisibleCellRef.current}
                                    />
                                    <GridColumn
                                        field="PDACanWrite"
                                        title="PDA Write"
                                        width={120}
                                        cell={milestoneLinePDACanWriteCellRef.current}
                                    />
                                    <GridColumn
                                        field="WebCanWrite"
                                        title="Web Write"
                                        width={120}
                                        cell={milestoneLineWebCanWriteCellRef.current}
                                    />
                                    <GridColumn
                                        width={85}
                                        filterable={false}
                                        sortable={false}
                                        cell={(props) => (
                                            <td style={{ padding: 0 }}>
                                                {props.dataItem.inEdit ? (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                            onClick={() => cancelEditMilestoneLine(props.dataItem as MilestoneLineRow)}
                                                            title="Cancel"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-cancel" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                            onClick={() => saveMilestoneLine(props.dataItem as MilestoneLineRow)}
                                                            title="Save"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-check" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-base"
                                                            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                            onClick={() => startEditMilestoneLine(props.dataItem as MilestoneLineRow)}
                                                            disabled={state.milestoneLinesEditingId !== null}
                                                            title="Edit"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-edit" />
                                                        </button>
                                                        <button
                                                            className="k-button k-button-sm k-rounded-md k-button-solid-error"
                                                            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: '6px 8px' }}
                                                            onClick={() => openMilestoneLineDeleteConfirm(props.dataItem.MilestoneLineID)}
                                                            disabled={state.milestoneLinesEditingId !== null || props.dataItem.MilestoneLineID <= 0}
                                                            title="Delete"
                                                        >
                                                            <span className="k-icon k-font-icon k-i-trash" />
                                                        </button>
                                                    </>
                                                )}
                                            </td>
                                        )}
                                    />
                                </Grid>

                                <button
                                    className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                    onClick={addMilestoneLine}
                                    disabled={state.milestoneLinesEditingId !== null}
                                    title="Add Milestone Line"
                                    style={{ marginTop: '8px' }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" /> Add Milestone Line
                                </button>
                            </div>
                        )}
                    </div>

                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeMilestoneLinesDialog}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.milestoneLinesDeleteConfirmId !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeMilestoneLineDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this milestone line?
                    </p>
                    {state.milestoneLinesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.milestoneLinesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmMilestoneLineDelete}
                            disabled={state.milestoneLinesDeleteInProgress}
                        >
                            {state.milestoneLinesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeMilestoneLineDeleteConfirm}
                            disabled={state.milestoneLinesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.allowedItemTypesDlgOpen && state.allowedItemTypesMilestone && (
                <Dialog
                    title={`Allowed Item Types For ${state.allowedItemTypesMilestone.milestoneDesc || state.allowedItemTypesMilestone.milestoneCode || `ID ${state.allowedItemTypesMilestone.milestoneID}`}`}
                    onClose={closeAllowedItemTypesDialog}
                    width={500}
                    height={600}
                >
                    <div className="tmi-dialog-list-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        {state.allowedItemTypesDlgError && (
                            <div className="tmi-dialog-list-error">
                                {state.allowedItemTypesDlgError}
                            </div>
                        )}
                        {state.allowedItemTypesDlgLoading ? (
                            <div className="tmi-dialog-list-loading">
                                <span className="k-icon k-font-icon k-i-loading" />
                                Loading item types...
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    className="k-input tmi-dialog-list-filter"
                                    placeholder="Filter item types..."
                                    value={state.allowedItemTypesDlgFilterText}
                                    onChange={handleAllowedItemTypesFilterChange}
                                />
                                <div className="tmi-dialog-list-content" style={{ flex: 1, overflowY: 'auto' }}>
                                    {state.allowedItemTypesDropdownData
                                        .filter(itemType => itemType.display.toLowerCase().includes(state.allowedItemTypesDlgFilterText.toLowerCase()))
                                        .map((itemType) => (
                                            <div key={itemType.value} className="tmi-dialog-list-item">
                                                <input
                                                    type="checkbox"
                                                    id={`allowed-item-type-${itemType.value}`}
                                                    checked={state.allowedItemTypesSelected.has(itemType.value)}
                                                    onChange={() => toggleAllowedItemTypeSelection(itemType.value)}
                                                />
                                                <label htmlFor={`allowed-item-type-${itemType.value}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                                    {itemType.display}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAllowedItemTypesDialog}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.allowedPacklistTypesDlgOpen && state.allowedPacklistTypesMilestone && (
                <Dialog
                    title={`Allowed Packlist Types For ${state.allowedPacklistTypesMilestone.milestoneDesc || state.allowedPacklistTypesMilestone.milestoneCode || `ID ${state.allowedPacklistTypesMilestone.milestoneID}`}`}
                    onClose={closeAllowedPacklistTypesDialog}
                    width={500}
                    height={600}
                >
                    <div className="tmi-dialog-list-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                        {state.allowedPacklistTypesDlgError && (
                            <div className="tmi-dialog-list-error">
                                {state.allowedPacklistTypesDlgError}
                            </div>
                        )}
                        {state.allowedPacklistTypesDlgLoading ? (
                            <div className="tmi-dialog-list-loading">
                                <span className="k-icon k-font-icon k-i-loading" />
                                Loading packlist types...
                            </div>
                        ) : (
                            <>
                                <input
                                    type="text"
                                    className="k-input tmi-dialog-list-filter"
                                    placeholder="Filter packlist types..."
                                    value={state.allowedPacklistTypesDlgFilterText}
                                    onChange={handleAllowedPacklistTypesFilterChange}
                                />
                                <div className="tmi-dialog-list-content" style={{ flex: 1, overflowY: 'auto' }}>
                                    {state.allowedPacklistTypesDropdownData
                                        .filter(packlistType => packlistType.display.toLowerCase().includes(state.allowedPacklistTypesDlgFilterText.toLowerCase()))
                                        .map((packlistType) => (
                                            <div key={packlistType.value} className="tmi-dialog-list-item">
                                                <input
                                                    type="checkbox"
                                                    id={`allowed-packlist-type-${packlistType.value}`}
                                                    checked={state.allowedPacklistTypesSelected.has(packlistType.value)}
                                                    onChange={() => toggleAllowedPacklistTypeSelection(packlistType.value)}
                                                />
                                                <label htmlFor={`allowed-packlist-type-${packlistType.value}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                                    {packlistType.display}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button className="k-button k-button-md k-rounded-md" onClick={closeAllowedPacklistTypesDialog}>
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.userRolesDlgOpen && state.userRolesMilestone && (
                <Dialog
                    title={`User Roles for ${state.userRolesMilestone.milestoneDesc || state.userRolesMilestone.milestoneCode || `ID ${state.userRolesMilestone.milestoneID}`}`}
                    onClose={closeUserRolesDialog}
                    width={700}
                    height={550}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '410px' }}>
                        {state.userRolesDlgError && (
                            <div style={{ color: '#d32f2f', marginBottom: '8px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px', fontSize: '12px' }}>
                                {state.userRolesDlgError}
                            </div>
                        )}
                        {state.userRolesDlgLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <span className="k-icon k-font-icon k-i-loading" />
                                <span style={{ marginLeft: '8px' }}>Loading...</span>
                            </div>
                        ) : (
                            <>
                                <div style={{ flex: 1, overflow: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                    {state.userRolesData.length === 0 ? (
                                        <div style={{ padding: '12px', textAlign: 'center', color: '#666', fontSize: '12px' }}>
                                            No user roles assigned
                                        </div>
                                    ) : (
                                        <Grid data={state.userRolesData} className="tmi-editor-grid" dataItemKey="clientRowKey">
                                            <GridColumn
                                                field="display"
                                                title="User Role"
                                                cell={(cellProps) => {
                                                    return (
                                                        <td className="tmi-dialog-grid-text-cell">
                                                            {cellProps.dataItem.inEdit ? (
                                                                <select
                                                                    className="k-input k-input-sm"
                                                                    value={cellProps.dataItem.UserRoleID ?? ''}
                                                                    onChange={(e) => {
                                                                        const selectedRoleId = e.target.value ? parseInt(e.target.value, 10) : null;
                                                                        const selectedRole = state.userRolesDropdownData.find(r => Number(r.value) === selectedRoleId);
                                                                        setState(prev => ({
                                                                            ...prev,
                                                                            userRolesData: prev.userRolesData.map(item =>
                                                                                item.clientRowKey === cellProps.dataItem.clientRowKey
                                                                                    ? { ...item, UserRoleID: selectedRoleId, display: selectedRole?.display || '' }
                                                                                    : item
                                                                            ),
                                                                        }));
                                                                    }}
                                                                    style={{ width: '100%' }}
                                                                >
                                                                    <option value="">-- Select a User Role --</option>
                                                                    {state.userRolesDropdownData
                                                                        .filter(role => {
                                                                            const roleId = Number(role.value);
                                                                            const isCurrent = roleId === cellProps.dataItem.UserRoleID;
                                                                            const isUsed = state.userRolesData.some(r => r.UserRoleID === roleId && r.clientRowKey !== cellProps.dataItem.clientRowKey);
                                                                            return isCurrent || !isUsed;
                                                                        })
                                                                        .map(role => (
                                                                            <option key={role.value} value={role.value}>
                                                                                {role.display}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                            ) : (
                                                                cellProps.dataItem.display
                                                            )}
                                                        </td>
                                                    );
                                                }}
                                            />
                                            <GridColumn
                                                title="Can Scan"
                                                width={90}
                                                cell={(cellProps) => (
                                                    <td style={{ padding: '6px', textAlign: 'center' }}>
                                                        {cellProps.dataItem.inEdit ? (
                                                            <input
                                                                type="checkbox"
                                                                checked={!!cellProps.dataItem.CanScan}
                                                                onChange={(e) => {
                                                                    setState(prev => ({
                                                                        ...prev,
                                                                        userRolesData: prev.userRolesData.map(item =>
                                                                            item.clientRowKey === cellProps.dataItem.clientRowKey
                                                                                ? { ...item, CanScan: e.target.checked }
                                                                                : item
                                                                        ),
                                                                    }));
                                                                }}
                                                            />
                                                        ) : (
                                                            <input
                                                                type="checkbox"
                                                                checked={!!cellProps.dataItem.CanScan}
                                                                disabled={true}
                                                            />
                                                        )}
                                                    </td>
                                                )}
                                            />
                                            <GridColumn
                                                title="Actions"
                                                width={90}
                                                cell={(cellProps) => (
                                                    <td style={{ padding: '4px', textAlign: 'center' }}>
                                                        {cellProps.dataItem.inEdit ? (
                                                            <>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                    onClick={() => cancelEditUserRoleMilestone(cellProps.dataItem as UserRoleMilestoneRow)}
                                                                    disabled={state.userRolesDlgLoading}
                                                                    title="Cancel"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-cancel" />
                                                                </button>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-success tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                    onClick={() => saveUserRoleMilestone(cellProps.dataItem as UserRoleMilestoneRow)}
                                                                    disabled={state.userRolesDlgLoading}
                                                                    title="Save"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-check" />
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first"
                                                                    onClick={() => startEditUserRoleMilestone(cellProps.dataItem as UserRoleMilestoneRow)}
                                                                    disabled={state.userRolesDlgLoading || state.userRolesEditingKey !== null}
                                                                    title="Edit"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-edit" />
                                                                </button>
                                                                <button
                                                                    className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next"
                                                                    onClick={() => openUserRoleMilestoneDeleteConfirm(cellProps.dataItem as UserRoleMilestoneRow)}
                                                                    disabled={state.userRolesDlgLoading || state.userRolesEditingKey !== null}
                                                                    title="Delete"
                                                                >
                                                                    <span className="k-icon k-font-icon k-i-trash" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </td>
                                                )}
                                            />
                                        </Grid>
                                    )}
                                </div>
                                <button
                                    disabled={state.userRolesData.some(r => r.inEdit) || state.userRolesDlgLoading}
                                    className="k-button k-button-sm k-rounded-md k-button-solid-primary tmi-dialog-add-button"
                                    onClick={addUserRoleMilestone}
                                    title="Add new User Role"
                                    style={{ marginTop: '8px' }}
                                >
                                    <span className="k-icon k-font-icon k-i-plus" /> Add User Role
                                </button>
                            </>
                        )}
                    </div>
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUserRolesDialog}
                            disabled={state.userRolesDlgLoading}
                        >
                            Close
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.userRolesDeleteConfirmKey !== null && (
                <Dialog
                    title="Confirm Delete"
                    onClose={closeUserRoleMilestoneDeleteConfirm}
                    width={420}
                >
                    <p style={{ padding: '8px 0' }}>
                        Are you sure you want to delete this user role assignment?
                    </p>
                    {state.userRolesDeleteError && (
                        <div style={{ color: '#d32f2f', marginBottom: '12px', padding: '8px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
                            {state.userRolesDeleteError}
                        </div>
                    )}
                    <DialogActionsBar>
                        <button
                            className="k-button k-button-md k-rounded-md k-button-solid-error"
                            onClick={confirmUserRoleMilestoneDelete}
                            disabled={state.userRolesDeleteInProgress}
                        >
                            {state.userRolesDeleteInProgress ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            className="k-button k-button-md k-rounded-md"
                            onClick={closeUserRoleMilestoneDeleteConfirm}
                            disabled={state.userRolesDeleteInProgress}
                        >
                            Cancel
                        </button>
                    </DialogActionsBar>
                </Dialog>
            )}

            {state.menuDataItem && (
                <div
                    ref={menuRef}
                    onMouseLeave={handleMenuBlur}
                    onMouseEnter={() => {
                        if (menuBlurTimeoutRef.current) window.clearTimeout(menuBlurTimeoutRef.current);
                    }}
                    style={{
                        position: 'fixed',
                        left: state.menuPosition ? `${state.menuPosition.left}px` : '-9999px',
                        top: state.menuPosition ? `${state.menuPosition.top}px` : '-9999px',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '200px',
                    }}
                >
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('milestoneLines')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Milestone Lines
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('allowedItemTypes')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Allowed Item Types
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('allowedPacklistTypes')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        Allowed Packlist Types
                    </div>
                    <div
                        style={{
                            padding: '6px 12px',
                            fontSize: '14px',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleMenuItemClick('userRoles')}
                        onMouseDown={e => e.preventDefault()}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                        User roles
                    </div>
                </div>
            )}
        </div>
    );
};

export default connector(Milestones);
